import { FC, useState, useEffect, useMemo, memo } from 'react';
import classNames from 'classnames';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { fetchCurrentUserData, selectAccountLoading } from 'application/store';
import { useAppDispatch, useAppSelector } from 'application/providers/StoreProvider';
import SelectableList from 'application/components/SelectableList';
import PointItemContent from 'application/components/PointItemContent';
import EmptyState from 'application/components/EmptyState';
import { IdType } from 'application/pages/types';
import { VideoPriceInfo } from 'application/components/VideoPricePlan';

import { PointShortTypeFragment } from 'domain/api/graphql/generated';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Switcher, { LabelPlacement } from '@zvuk-b2b/react-uikit/ui/Switcher';
import InputSearch from '@zvuk-b2b/react-uikit/ui/InputSearch';

import { usePointsTabPresenter } from '../presenters/PointsTabPresenter';
import { useVideoStreamsPointsController } from '../controllers/VideoStreamsPointsController';
import { TEXTS } from '../../texts';
import { ActionButtons } from './components';

import styles from './VideoStreamBindModal.module.scss';

const VIDEO_TEMP_FF = true;

const VideoStreamBindModal: FC<ModalProps[ModalTypes.VIDEO_STREAM_BIND]> = ({
  selectedPoints: initialSelectedPoints,
  videoStreamId,
  close,
  openModal,
  replaceModal,
  selectedPoint,
  isDenyCreatePoint,
  setCurrentSelectedPointPoint,
}) => {
  const dispatch = useAppDispatch();

  const isAccountLoading = useAppSelector(selectAccountLoading);

  const { allPointList, loadMore } = usePointsTabPresenter({ isVideoDemo: null });

  const { isIpad } = useMedia();

  const { selectedPoints, onPointsChange, isButtonDisabled } = useVideoStreamsPointsController({
    initialSelectedPoints,
    points: allPointList.points || [],
  });

  const [search, setSearch] = useState<string>(selectedPoint?.pointAddress || '');
  const [isSelectedPointSet, setIsSelectedPointSet] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchCurrentUserData());
  }, []);

  const isAllPointsLoading = allPointList.loading || allPointList.isReloading || isAccountLoading;

  const currentItems = (allPointList.points || []).filter(
    (item) =>
      item.address?.toLowerCase().includes(search.toLowerCase()) ||
      item.tags?.find((tag) => tag.title.toLowerCase().includes(search.toLowerCase()))
  );

  const selectedItems = useMemo(() => {
    const result = new Map<string, IdType>();

    selectedPoints.forEach((point) => {
      result.set(point.id, { id: point.id });
    });

    return result;
  }, [selectedPoints]);

  const hasSelectedItems = selectedItems.size > 0;
  const hasUnselectedItems = currentItems.length > selectedItems.size;

  const onSearchChange = (value: string) => {
    setSearch(value);
  };

  const onChangeWrapper = (value: boolean, point: PointShortTypeFragment) => {
    if (value) {
      onPointsChange([...selectedPoints, point]);
    } else {
      onPointsChange(selectedPoints.filter((current) => current.id !== point.id));
    }
  };

  const onChangeAll = (value: boolean) => {
    if (value) {
      onPointsChange(currentItems);
      return;
    }

    const filteredItems = selectedPoints.filter((current) => !currentItems.some((item) => item.id === current.id));

    onPointsChange(filteredItems);
  };

  useEffect(() => {
    if (selectedPoint && currentItems.length && !isSelectedPointSet) {
      const currentPoint = currentItems.find((item) => item.id === selectedPoint?.pointId);

      setIsSelectedPointSet(true);
      if (currentPoint) {
        onChangeWrapper(true, currentPoint);
      }
    }
  }, [selectedPoint?.pointId, currentItems, isSelectedPointSet]);

  useEffect(() => {
    if (!search && selectedPoint && isSelectedPointSet) {
      setCurrentSelectedPointPoint?.(undefined);
    }
  }, [search, selectedPoint?.pointId, isSelectedPointSet]);

  return (
    <ModalScreen
      title={TEXTS.MODAL_BIND_TITLE}
      titlePlacement="left"
      disableScroll={!isIpad}
      contentClassName={classNames(styles.content, {
        [styles.contentEmpty]: VIDEO_TEMP_FF && allPointList.points?.length === 0,
      })}
      onClose={close}
      actionButtons={
        <ActionButtons
          videoStreamId={videoStreamId}
          isBindButtonDisabled={isButtonDisabled}
          isDenyCreatePoint={isDenyCreatePoint}
          withDemoPeriod={VIDEO_TEMP_FF}
          showRequestVideoButton={VIDEO_TEMP_FF && allPointList.points?.length === 0}
          selectedPoints={selectedPoints}
          selectedPoint={selectedPoint}
          allPointsIds={(allPointList.points || []).map((point) => point.id)}
          openModal={openModal}
          replaceModal={replaceModal}
          close={close}
        />
      }>
      {VIDEO_TEMP_FF && allPointList.points?.length === 0 ? (
        <div>
          <div className={styles.noAddress}>{TEXTS.MODAL_BIND_NO_ADDRESS}</div>
          <VideoPriceInfo />
        </div>
      ) : (
        <>
          <InputSearch
            containerClassName={styles.searchInput}
            value={search}
            elementId="toolbar_input_search"
            onChange={onSearchChange}
            isLoading={isAllPointsLoading}
            placeholder={TEXTS.SEARCH_PLACEHOLDER}
          />

          {allPointList.points?.length !== 0 && (
            <SelectableList<PointShortTypeFragment>
              elementId="checkbox_item_bind"
              listWrapperClassName={classNames(styles.listWrapper, {
                [styles.listWrapperLoading]: isAllPointsLoading,
              })}
              items={currentItems}
              itemKeyForRender="address"
              itemContent={
                <PointItemContent
                  showVideoStreamData
                  isLoading={isAllPointsLoading}
                  hintText={TEXTS.MODAL_BIND_DISABLED_POINT_HINT_TEXT}
                  hintIconNearSelector
                />
              }
              labelPlacement={LabelPlacement.LEFT}
              itemsLoading={isAllPointsLoading}
              isLoading={isAllPointsLoading}
              selectorDisabled={isAllPointsLoading}
              selectedItems={selectedItems}
              onChange={onChangeWrapper}
              loadMore={loadMore}
              paginationInfo={allPointList.paginationInfo}
              totalItemsCount={allPointList.points?.length || 0}
              hasMultiselect
              hasSelectedItems={hasSelectedItems}
              hasUnSelectedItems={hasUnselectedItems}
              onChangeAll={onChangeAll}
              selectorRenderer={Switcher}
              headerClassName={styles.listHeader}
              headerLeftSlot={null}
            />
          )}

          {allPointList.points?.length === 0 && (
            <EmptyState>
              <span>{TEXTS.POINTS_LIST_EMPTY}</span>
            </EmptyState>
          )}
        </>
      )}
    </ModalScreen>
  );
};

export default memo(VideoStreamBindModal);
